/**
 * Re-arms the lazy machinery of the page after a product swap.
 *
 * The flavor switcher replaces <main> without a page load. Two things on the page collect the
 * elements they care about once, into an IntersectionObserver, and never look again: the
 * theme's <animate-on-load> and Okendo's reviews widget. Both live -- or were set up -- outside
 * the markup the swap replaces, so they keep watching nodes that are on their way out and
 * never see what arrives in their place.
 *
 * This file is loaded by the alt variant selector block, inside <main>, which is what gets it
 * re-executed on each swap: the swap re-creates the script tags it replaces.
 */
(() => {
    // The outgoing markup is hidden with an inline display:none and removed half a second
    // later, so it is still queryable while this runs. Nothing here should touch it.
    function isStale(node) {
        for (let element = node; element; element = element.parentElement) {
            if (element.style?.display === 'none') return true;
        }

        return false;
    }

    /**
     * Block reveal animations. <animate-on-load> (theme.liquid, outside <main>) observes every
     * .animate_on_load element in connectedCallback and adds .animate_on_load_complete as they
     * scroll into view; until that lands, mobile.css keeps the titles, text and images inside
     * them at opacity 0. A Content Grid whose items carry the class therefore comes back from
     * a swap as an empty coloured band.
     *
     * Replacing the element re-runs the theme's own collection pass over the markup that is on
     * the page now, animation and all.
     */
    // Once per swap: the second pass below is there for Okendo, which re-renders its widget on
    // its own schedule, and replacing the element again would only churn its observer.
    let rearmed = false;

    function rearmRevealAnimations() {
        if (rearmed) return;

        const element = document.querySelector('animate-on-load');
        if (!element) return;

        const pending = Array.from(document.querySelectorAll('.animate_on_load:not(.animate_on_load_complete)'));
        if (!pending.some((node) => !isStale(node))) return;

        element.replaceWith(document.createElement('animate-on-load'));
        rearmed = true;
    }

    /**
     * The Okendo app block is server-rendered, so its contents look complete immediately after
     * the swap. Its controls are not functional yet, however: Okendo scanned the document only
     * on the original page load, and all of its event handlers belonged to the removed widget.
     * Re-run Okendo's public widget initialiser for each fresh, visible widget. Besides binding
     * sorting and "Show more", this lets Okendo own lazy images added by later review requests.
     */
    function rearmOkendoWidgets() {
        const initReviewsWidget = window.okeReviewsWidgetApi?.initReviewsWidget;
        if (typeof initReviewsWidget !== 'function') return;

        document.querySelectorAll('[data-oke-reviews-widget]').forEach((widget) => {
            if (isStale(widget) || widget.okeDestroyHandlers) return;

            Promise.resolve(initReviewsWidget(widget)).catch((error) => {
                console.error('Unable to initialise the swapped Okendo reviews widget.', error);
            });
        });
    }

    /**
     * Okendo review images. The app renders them with a transparent placeholder in src and the
     * real file in data-oke-reviews-lazy-image-url, and its own script fills them in as they
     * come into view -- so after a swap the review photos and the reviewed-product thumbnails
     * stay blank. This is that same swap, done for the images the app is no longer watching.
     */
    function rearmOkendoImages() {
        const images = Array.from(document.querySelectorAll('img[data-oke-reviews-lazy-image-url]'))
            // A src that is not the placeholder is an image somebody has already loaded.
            .filter((img) => img.getAttribute('src')?.startsWith('data:'))
            .filter((img) => !isStale(img));

        if (!images.length) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                observer.unobserve(entry.target);
                entry.target.src = entry.target.dataset.okeReviewsLazyImageUrl;
            });
        }, { rootMargin: '200px' });

        images.forEach((img) => observer.observe(img));
    }

    function rehydrate() {
        rearmRevealAnimations();
        rearmOkendoWidgets();
        rearmOkendoImages();
    }

    // After the next paint, so the fresh markup has been laid out and the observers have real
    // positions to work from.
    requestAnimationFrame(rehydrate);
    setTimeout(rehydrate, 600);
})();
